import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  apiDeleteDash,
  apiGetDashes,
} from "../../services/DashAPI";
import {
  Chart,
  Dash,
  
} from "../../types/Dash";

import {
  Folder,
} from "../../types/Folder";

import {
  Role,
} from "../../types/Role";

import {
  Host,
} from "../../types/Host";

import {
  Factotum,
} from "../../types/Factotum";

import {
  Project,
} from "../../types/Project";

import {
  ProjectTask,
  ProjectTaskInput,
  ProjectTaskOutput,
} from "../../types/ProjectTask";

import {
  FactotumState,
} from "../../types/FactotumState";

import {
  FactotumStateTie,
} from "../../types/FactotumStateTie";

import {
  Automation,
} from "../../types/Automation";

import {
  AutoLine,
} from "../../types/AutoLine";

import {
  Repo,
} from "../../types/Repo";

import {
  User,
} from "../../types/User";

import {
  Bookmark,
} from "../../types/Bookmark";

import {
  Measurable,
} from "../../types/Scenario";

import DocumentTemplateInfoForm from "./DashInfoForm";

export interface SearchResult {
  path: string;
}

// A function/method extracted by the code-structure mapper.
export interface FunctionInfo {
  method_id: number;
  name: string;
  method_kind: string;
  signature: string | null;
  class_name: string | null;
  file_path: string | null;
  line_number: number;
  outbound: number;
  inbound: number;
  transitive_outbound: number;
  transitive_inbound: number;
}

export interface FuncMapStats {
  files: number;
  classes: number;
  methods: number;
  function_calls: number;
  properties: number;
  dependencies: number;
  resolved_pct: number;
}

// One node of a full program slice (a function and its source block), with its
// distance (depth) from the sliced function.
export interface SliceBlock {
  depth: number;
  method_id: number;
  name: string;
  signature: string | null;
  return_type: string | null;
  class_name: string | null;
  file_path: string | null;
  line_number: number;
  is_external: boolean;
  is_cycle: boolean;
  call_count: number;
  start_line: number | null;
  end_line: number | null;
  code: string | null;
}

// --- Dependency network chart ---
// A node is either a file ("f<fileId>") or a function ("m<methodId>"). The graph
// starts as file nodes and is expanded on demand (file -> its functions ->
// callee functions across files).
// Sizing point-of-view for the network chart.
//   usage       = how often it's used (inbound dependency count) — DEFAULT
//   functions   = function count (files) / fan-out (functions)
//   connections = graph degree (edges touching the node)
//   altitude    = how high in the call hierarchy (transitive reach down − up)
export type NetworkPov = "usage" | "functions" | "connections" | "altitude";

// Top-level rendering mode for the graph view. STRUCTURAL is the original
// file/function dependency graph (untouched). FEATURE renders the semantic
// overlay: launch points and their flow spines.
export type NetworkMode = "structural" | "feature";

export interface NetworkNode {
  id: string;
  kind: "file" | "function";
  label: string;
  fileId?: number;
  methodId?: number;
  filePath?: string | null;
  lineNumber?: number;
  language?: string;
  methodCount?: number;
  // Inbound dependency count (how often used).
  usage?: number;
  // Outbound dependency count (how much it calls out).
  fanout?: number;
  // Transitive reach up/down the call graph (for "altitude").
  transitiveIn?: number;
  transitiveOut?: number;
  // Relative size hint (file: method count; function: call count / fan-out).
  weight?: number;
}

export interface NetworkEdge {
  source: string;
  target: string;
  weight: number;
}

// Index-job stats payload: live progress fields while running, final totals when
// completed (the worker writes one shape during the run, the other at the end).
export interface IndexStats {
  // live progress
  phase?: string;
  files_done?: number;
  files_total?: number;
  current_file?: string;
  // final totals
  files?: number;
  classes?: number;
  methods?: number;
  function_calls?: number;
  properties?: number;
  dependencies?: number;
  resolved_pct?: number;
}

export interface IndexStatus {
  job_id?: number;
  job_type?: string;
  status: "none" | "queued" | "running" | "completed" | "failed";
  stats?: IndexStats | null;
  error?: string | null;
  // Bumped on every worker write — used as a liveness heartbeat by the UI.
  updated_at?: string;
}

export interface LibraryState {
  dashes: Dash[];
  folders: Folder[];
  roles: Role[];
  hosts: Host[];
  factotums: Factotum[];
  projects: Project[];
  projectTasks: ProjectTask[];
  projectTaskInputs: ProjectTaskInput[];
  projectTaskOutputs: ProjectTaskOutput[];
  factotumStates: FactotumState[];
  factotumStateTies: FactotumStateTie[];
  automations: Automation[];
  auto_lines: AutoLine[];
  repos: Repo[];
  bookmarks: Bookmark[];
  files: string[];
  fileSizes: number[];
  fileModifieds: string[];
  file_folders: string[];
  // Map of folder full-path -> label (LIVE_CHANGES | ESTABLISHED | DOCUMENTS | LEGACY | TO_DIE).
  fileFolderLabels: { [path: string]: string };
  // Map of file path -> line count, lazily filled for text files the user is looking at.
  fileLineCounts: { [path: string]: number };

  // "Map functions" feature: per-file function map + dependency expansion.
  funcMapOpen: boolean;
  funcMapStatus: "idle" | "mapping" | "done" | "failed";
  funcMapFile: string;
  funcMapRepoId: number;
  funcMapStats: FuncMapStats | null;
  funcMapMappedFiles: string[];
  funcMapFunctions: FunctionInfo[];
  funcMapCallees: { [methodId: number]: FunctionInfo[] };
  funcMapError: string | undefined;
  // Full program slice for a clicked function (sorted descending by distance).
  funcMapSliceFor: number | null;
  funcMapSliceName: string;
  funcMapSliceStatus: "idle" | "loading" | "done" | "failed";
  funcMapSliceBlocks: SliceBlock[];
  // "down" = calls (callees), "up" = used-by (callers / reverse slice).
  funcMapSliceDirection: "down" | "up";

  // --- Dependency network chart ---
  networkOpen: boolean;
  networkRepoId: number;
  networkStatus: "idle" | "loading" | "done" | "failed";
  networkError: string | undefined;
  networkNodes: NetworkNode[];
  networkEdges: NetworkEdge[];
  // Which file/function nodes have already been expanded (so we don't refetch).
  networkExpandedFiles: number[];
  networkExpandedFunctions: number[];
  // The currently selected node id (for the side detail panel). This is the
  // "primary" of the multi-selection — always also present in networkMultiSelected.
  networkSelected: string | null;
  // Full multi-selection set (shift-click / box-select). Includes networkSelected.
  networkMultiSelected: string[];
  // User-assigned group colours, keyed by node id (survives expand/collapse).
  networkNodeColors: Record<string, string>;
  // When set, the graph shows ONLY functions (a focused drill-in on this file),
  // hiding the file-level graph. null = normal file graph.
  networkFocusFile: string | null;
  // Sizing point-of-view + hidden directory prefixes (persisted per repo).
  networkPov: NetworkPov;
  networkMode: NetworkMode;
  networkHiddenDirs: string[];
  // "Show code" modal (right-click a node).
  networkCodeOpen: boolean;
  networkCodeTitle: string;
  networkCodeSubtitle: string;
  networkCodeBody: string;
  networkCodeStatus: "idle" | "loading" | "done" | "failed";
  // Latest background-index job status for the open repo.
  indexStatus: IndexStatus | null;

  gitFolderInfo: string;

  folderName: string;

  // The current folder in a workspace
  currentFolderId: number;
  folderStack: Array<number>;
  folderNameStack: Array<string>;
  
  status: "idle" | "loading" | "failed";
  error: string | undefined;
  preview: string | undefined;
  automationOutput: string | undefined;
  gitOutput: { stdout: string; stderr: string } | undefined;
  filterString: string;
  codeSearchString: string;
  viewDeleted: boolean;

  searchMode: boolean;
  searchResult: SearchResult[];

  reposMode: boolean;
  repoId: number;
  repoFolder: string;

  scenariosMode: boolean;
  scenarioId: number;
  scenarioMeasurables: Measurable[];

  rolesMode: boolean;
  roleId: number;
  usersInRole: Array<User>;

  focusChart: null | Chart;

  // Indicates when the repo file list is being refreshed
  isFileListRefreshing: boolean;

  // File list pagination (from file_list API full_length)
  fileListFullLength: number;
  fileListPageStart: number;
  fileListPageSize: number;
}

const initialState: LibraryState = {
  dashes: [] as Dash[],
  folders: [] as Folder[],
  roles: [] as Role[],
  hosts: [] as Host[],
  factotums: [] as Factotum[],
  projects: [] as Project[],
  projectTasks: [] as ProjectTask[],
  projectTaskInputs: [] as ProjectTaskInput[],
  projectTaskOutputs: [] as ProjectTaskOutput[],
  factotumStates: [] as FactotumState[],
  factotumStateTies: [] as FactotumStateTie[],
  automations: [] as Automation[],
  auto_lines: [] as AutoLine[],
  repos: [] as Repo[],
  bookmarks: [] as Bookmark[],
  files: [] as string[],
  fileSizes: [] as number[],
  fileModifieds: [] as string[],
  file_folders: [] as string[],
  fileFolderLabels: {} as { [path: string]: string },
  fileLineCounts: {} as { [path: string]: number },

  funcMapOpen: false,
  funcMapStatus: "idle" as "idle" | "mapping" | "done" | "failed",
  funcMapFile: "",
  funcMapRepoId: -1,
  funcMapStats: null as FuncMapStats | null,
  funcMapMappedFiles: [] as string[],
  funcMapFunctions: [] as FunctionInfo[],
  funcMapCallees: {} as { [methodId: number]: FunctionInfo[] },
  funcMapError: undefined as string | undefined,
  funcMapSliceFor: null as number | null,
  funcMapSliceName: "",
  funcMapSliceStatus: "idle" as "idle" | "loading" | "done" | "failed",
  funcMapSliceBlocks: [] as SliceBlock[],
  funcMapSliceDirection: "down" as "down" | "up",

  networkOpen: false,
  networkRepoId: -1,
  networkStatus: "idle" as "idle" | "loading" | "done" | "failed",
  networkError: undefined as string | undefined,
  networkNodes: [] as NetworkNode[],
  networkEdges: [] as NetworkEdge[],
  networkExpandedFiles: [] as number[],
  networkExpandedFunctions: [] as number[],
  networkSelected: null as string | null,
  networkMultiSelected: [] as string[],
  networkNodeColors: {} as Record<string, string>,
  networkFocusFile: null as string | null,
  networkPov: "usage" as NetworkPov,
  networkMode: "structural" as NetworkMode,
  networkHiddenDirs: [] as string[],
  networkCodeOpen: false,
  networkCodeTitle: "",
  networkCodeSubtitle: "",
  networkCodeBody: "",
  networkCodeStatus: "idle" as "idle" | "loading" | "done" | "failed",
  indexStatus: null as IndexStatus | null,

  gitFolderInfo: "",

  folderName: "/",

  // We start at the root folder
  currentFolderId: -1,
  folderStack: [],
  folderNameStack: [],
  
  status: "idle",
  error: undefined,
  preview: undefined,
  automationOutput: undefined,
  gitOutput: undefined,
  filterString: "",
  codeSearchString: "",
  viewDeleted: false,

  searchMode: false,
  searchResult: [],

  reposMode: false,
  repoId: -1,
  repoFolder: "",

  scenariosMode: false,
  scenarioId: -1,
  scenarioMeasurables: [],

  fileListFullLength: 0,
  fileListPageStart: 0,
  fileListPageSize: 20,

  rolesMode: false,
  roleId: -1,
  usersInRole: [],

  focusChart: null,

  isFileListRefreshing: false,
};

export const deleteDash = createAsyncThunk<
  { dashes: Dash[] },
  {
    organizationId: number;
    workspaceId: number;
    currentFolderId: number;
    dash: Dash;
  }
>("library/deleteDocumentTemplate", async (params) => {
  const response = await apiDeleteDash(
    params.organizationId,
    params.workspaceId,
    params.dash
  );
  const fetchLatestResponse = await apiGetDashes(
    params.organizationId,
    params.workspaceId,
    params.currentFolderId
  );
  // The value we return becomes the `fulfilled` action payload
  return fetchLatestResponse.data;
});

export const librarySlice = createSlice({
  name: "library",
  initialState,
  reducers: {
    setStatus: (
      state,
      action: PayloadAction<{
        status: "idle" | "loading" | "failed";
        error: string | undefined;
      }>
    ) => {
      state.status = action.payload.status;
      state.error = action.payload.error;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    setDashes: (
      state,
      action: PayloadAction<Dash[]>
    ) => {
      state.dashes = action.payload;
    },
    setFolders: (
      state,
      action: PayloadAction<Folder[]>
    ) => {
      state.folders = action.payload;
    },
    setRoles: (
      state,
      action: PayloadAction<Role[]>
    ) => {
      state.roles = action.payload;
    },
    setHosts: (
      state,
      action: PayloadAction<Host[]>
    ) => {
      state.hosts = action.payload;
    },
    setFactotums: (
      state,
      action: PayloadAction<Factotum[]>
    ) => {
      state.factotums = action.payload;
    },
    setProjects: (
      state,
      action: PayloadAction<Project[]>
    ) => {
      state.projects = action.payload;
    },
    setProjectTasks: (
      state,
      action: PayloadAction<ProjectTask[]>
    ) => {
      state.projectTasks = action.payload;
    },
    setProjectTaskInputs: (
      state,
      action: PayloadAction<ProjectTaskInput[]>
    ) => {
      state.projectTaskInputs = action.payload;
    },
    setProjectTaskOutputs: (
      state,
      action: PayloadAction<ProjectTaskOutput[]>
    ) => {
      state.projectTaskOutputs = action.payload;
    },
    setFactotumStates: (
      state,
      action: PayloadAction<FactotumState[]>
    ) => {
      state.factotumStates = action.payload;
    },
    setFactotumStateTies: (
      state,
      action: PayloadAction<FactotumStateTie[]>
    ) => {
      state.factotumStateTies = action.payload;
    },
    setAutomations: (
      state,
      action: PayloadAction<Automation[]>
    ) => {
      state.automations = action.payload;
    },
    setAutoLines: (
      state,
      action: PayloadAction<AutoLine[]>
    ) => {
      state.auto_lines = action.payload;
    },
    setRepos: (
      state,
      action: PayloadAction<Repo[]>
    ) => {
      state.repos = action.payload;
    },
    setBookmarks: (
      state,
      action: PayloadAction<Bookmark[]>
    ) => {
      state.bookmarks = action.payload;
    },
    setFiles: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.files = action.payload;
    },
    setGitInfo: (
      state,
      action: PayloadAction<string>
    ) => {
      state.gitFolderInfo = action.payload;
    },
    setFileSizes: (
      state,
      action: PayloadAction<number[]>
    ) => {
      state.fileSizes = action.payload;
    },
    setFileModifieds: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.fileModifieds = action.payload;
    },
    setFileFolders: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.file_folders = action.payload;
    },
    // Merge labels returned from file_list so previously-seen folders keep their labels.
    setFileFolderLabels: (
      state,
      action: PayloadAction<{ [path: string]: string }>
    ) => {
      state.fileFolderLabels = { ...state.fileFolderLabels, ...action.payload };
    },
    // Set/clear a single folder's label (empty string clears it).
    setFileFolderLabel: (
      state,
      action: PayloadAction<{ path: string; label: string }>
    ) => {
      if (action.payload.label) {
        state.fileFolderLabels[action.payload.path] = action.payload.label;
      } else {
        delete state.fileFolderLabels[action.payload.path];
      }
    },
    // Merge lazily-computed line counts (file path -> count) for text files.
    setFileLineCounts: (
      state,
      action: PayloadAction<{ [path: string]: number }>
    ) => {
      state.fileLineCounts = { ...state.fileLineCounts, ...action.payload };
    },
    // --- Function map ---
    openFuncMap: (state, action: PayloadAction<{ filePath: string; repoId: number }>) => {
      state.funcMapOpen = true;
      state.funcMapFile = action.payload.filePath;
      state.funcMapRepoId = action.payload.repoId;
      state.funcMapStatus = "mapping";
      state.funcMapStats = null;
      state.funcMapMappedFiles = [];
      state.funcMapFunctions = [];
      state.funcMapCallees = {};
      state.funcMapError = undefined;
      state.funcMapSliceFor = null;
      state.funcMapSliceName = "";
      state.funcMapSliceStatus = "idle";
      state.funcMapSliceBlocks = [];
    },
    closeFuncMap: (state) => {
      state.funcMapOpen = false;
    },
    setFuncMapStatus: (
      state,
      action: PayloadAction<"idle" | "mapping" | "done" | "failed">
    ) => {
      state.funcMapStatus = action.payload;
    },
    setFuncMapResult: (
      state,
      action: PayloadAction<{
        file_path: string;
        stats: FuncMapStats | null;
        mapped_files: string[];
        functions: FunctionInfo[];
      }>
    ) => {
      state.funcMapFile = action.payload.file_path;
      state.funcMapStats = action.payload.stats;
      state.funcMapMappedFiles = action.payload.mapped_files;
      state.funcMapFunctions = action.payload.functions;
      state.funcMapStatus = "done";
      state.funcMapError = undefined;
    },
    setFuncMapError: (state, action: PayloadAction<string>) => {
      state.funcMapStatus = "failed";
      state.funcMapError = action.payload;
    },
    setFuncMapCallees: (
      state,
      action: PayloadAction<{ methodId: number; callees: FunctionInfo[] }>
    ) => {
      state.funcMapCallees[action.payload.methodId] = action.payload.callees;
    },
    // --- Program slice ---
    startFuncMapSlice: (
      state,
      action: PayloadAction<{ methodId: number; name: string; direction: "down" | "up" }>
    ) => {
      state.funcMapSliceFor = action.payload.methodId;
      state.funcMapSliceName = action.payload.name;
      state.funcMapSliceDirection = action.payload.direction;
      state.funcMapSliceStatus = "loading";
      state.funcMapSliceBlocks = [];
    },
    setFuncMapSlice: (state, action: PayloadAction<SliceBlock[]>) => {
      state.funcMapSliceBlocks = action.payload;
      state.funcMapSliceStatus = "done";
    },
    setFuncMapSliceError: (state) => {
      state.funcMapSliceStatus = "failed";
    },
    clearFuncMapSlice: (state) => {
      state.funcMapSliceFor = null;
      state.funcMapSliceName = "";
      state.funcMapSliceStatus = "idle";
      state.funcMapSliceBlocks = [];
    },
    // --- Dependency network chart ---
    openNetwork: (state, action: PayloadAction<{ repoId: number }>) => {
      state.networkOpen = true;
      state.networkError = undefined;
      // Only wipe the graph when switching to a different repo. Reopening the same
      // repo keeps whatever we already have on screen (a background re-index just
      // refreshes it in place) instead of flashing an empty view.
      if (state.networkRepoId !== action.payload.repoId) {
        state.networkNodes = [];
        state.networkEdges = [];
        state.networkExpandedFiles = [];
        state.networkExpandedFunctions = [];
        state.networkSelected = null;
        state.networkMultiSelected = [];
      }
      state.networkRepoId = action.payload.repoId;
      state.networkStatus = state.networkNodes.length > 0 ? "done" : "loading";
    },
    closeNetwork: (state) => {
      state.networkOpen = false;
    },
    setNetworkStatus: (
      state,
      action: PayloadAction<"idle" | "loading" | "done" | "failed">
    ) => {
      state.networkStatus = action.payload;
    },
    // Replace the whole graph (the initial file-level load).
    setNetworkGraph: (
      state,
      action: PayloadAction<{ nodes: NetworkNode[]; edges: NetworkEdge[] }>
    ) => {
      state.networkNodes = action.payload.nodes;
      state.networkEdges = action.payload.edges;
      state.networkStatus = "done";
      state.networkError = undefined;
    },
    setNetworkError: (state, action: PayloadAction<string>) => {
      state.networkStatus = "failed";
      state.networkError = action.payload;
    },
    // Merge expansion results (new nodes/edges), de-duplicated by id/source-target.
    addNetworkElements: (
      state,
      action: PayloadAction<{
        nodes: NetworkNode[];
        edges: NetworkEdge[];
        expandedFileId?: number;
        expandedFunctionId?: number;
      }>
    ) => {
      const seen = new Set(state.networkNodes.map((n) => n.id));
      for (const n of action.payload.nodes) {
        if (!seen.has(n.id)) {
          state.networkNodes.push(n);
          seen.add(n.id);
        }
      }
      const edgeKey = (e: NetworkEdge) => `${e.source}->${e.target}`;
      const seenEdges = new Set(state.networkEdges.map(edgeKey));
      for (const e of action.payload.edges) {
        if (!seenEdges.has(edgeKey(e))) {
          state.networkEdges.push(e);
          seenEdges.add(edgeKey(e));
        }
      }
      if (action.payload.expandedFileId !== undefined &&
          !state.networkExpandedFiles.includes(action.payload.expandedFileId)) {
        state.networkExpandedFiles.push(action.payload.expandedFileId);
      }
      if (action.payload.expandedFunctionId !== undefined &&
          !state.networkExpandedFunctions.includes(action.payload.expandedFunctionId)) {
        state.networkExpandedFunctions.push(action.payload.expandedFunctionId);
      }
    },
    // Plain select → replaces the whole selection with this single node.
    setNetworkSelected: (state, action: PayloadAction<string | null>) => {
      state.networkSelected = action.payload;
      state.networkMultiSelected = action.payload ? [action.payload] : [];
    },
    // Shift/ctrl-click → additive toggle of one node in the multi-selection.
    toggleNetworkSelected: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.networkMultiSelected.includes(id)) {
        state.networkMultiSelected = state.networkMultiSelected.filter((x) => x !== id);
        if (state.networkSelected === id) {
          state.networkSelected = state.networkMultiSelected[state.networkMultiSelected.length - 1] ?? null;
        }
      } else {
        state.networkMultiSelected.push(id);
        state.networkSelected = id;
      }
    },
    // Box-select → replace the whole selection with the given set (primary = last).
    setNetworkMultiSelected: (state, action: PayloadAction<string[]>) => {
      const ids = Array.from(new Set(action.payload));
      state.networkMultiSelected = ids;
      state.networkSelected = ids[ids.length - 1] ?? null;
    },
    clearNetworkSelected: (state) => {
      state.networkSelected = null;
      state.networkMultiSelected = [];
    },
    // Wipe every assigned colour.
    clearNetworkNodeColors: (state) => {
      state.networkNodeColors = {};
    },
    // Replace the whole colour map (used when loading persisted settings).
    setNetworkNodeColors: (state, action: PayloadAction<Record<string, string>>) => {
      state.networkNodeColors = action.payload ?? {};
    },
    // Collapse one expanded file: drop the function nodes belonging to it, then
    // cascade-prune any function nodes left orphaned (e.g. callees only reachable
    // through those functions). File nodes are always kept.
    collapseNetworkFile: (state, action: PayloadAction<number>) => {
      const fileId = action.payload;
      const fileNode = state.networkNodes.find((n) => n.kind === "file" && n.fileId === fileId);
      const path = fileNode?.filePath;
      if (path) {
        const removed = new Set(
          state.networkNodes.filter((n) => n.kind === "function" && n.filePath === path).map((n) => n.id)
        );
        if (removed.size > 0) {
          state.networkNodes = state.networkNodes.filter((n) => !removed.has(n.id));
          state.networkEdges = state.networkEdges.filter((e) => !removed.has(e.source) && !removed.has(e.target));
          let changed = true;
          while (changed) {
            changed = false;
            const connected = new Set<string>();
            for (const e of state.networkEdges) { connected.add(e.source); connected.add(e.target); }
            const orphans = new Set(
              state.networkNodes.filter((n) => n.kind === "function" && !connected.has(n.id)).map((n) => n.id)
            );
            if (orphans.size > 0) {
              state.networkNodes = state.networkNodes.filter((n) => !orphans.has(n.id));
              changed = true;
            }
          }
        }
      }
      state.networkExpandedFiles = state.networkExpandedFiles.filter((id) => id !== fileId);
      const present = new Set(state.networkNodes.map((n) => n.id));
      state.networkExpandedFunctions = state.networkExpandedFunctions.filter((mid) => present.has(`m${mid}`));
      if (state.networkSelected && !present.has(state.networkSelected)) state.networkSelected = null;
      state.networkMultiSelected = state.networkMultiSelected.filter((id) => present.has(id));
    },
    // Enter "function focus": drop everything back to the file graph, then mark
    // the focused file. The view will show ONLY that file's functions (loaded
    // separately) and hide the file-level graph.
    enterNetworkFocus: (state, action: PayloadAction<string>) => {
      const fileIds = new Set(state.networkNodes.filter((n) => n.kind === "file").map((n) => n.id));
      state.networkNodes = state.networkNodes.filter((n) => n.kind === "file");
      state.networkEdges = state.networkEdges.filter((e) => fileIds.has(e.source) && fileIds.has(e.target));
      state.networkExpandedFiles = [];
      state.networkExpandedFunctions = [];
      state.networkSelected = null;
      state.networkMultiSelected = [];
      state.networkFocusFile = action.payload;
    },
    // Exit focus → back to the file-level graph.
    exitNetworkFocus: (state) => {
      const fileIds = new Set(state.networkNodes.filter((n) => n.kind === "file").map((n) => n.id));
      state.networkNodes = state.networkNodes.filter((n) => n.kind === "file");
      state.networkEdges = state.networkEdges.filter((e) => fileIds.has(e.source) && fileIds.has(e.target));
      state.networkExpandedFiles = [];
      state.networkExpandedFunctions = [];
      state.networkSelected = null;
      state.networkMultiSelected = [];
      state.networkFocusFile = null;
    },
    // Collapse everything back to the file-level graph.
    collapseAllNetwork: (state) => {
      const fileIds = new Set(state.networkNodes.filter((n) => n.kind === "file").map((n) => n.id));
      state.networkNodes = state.networkNodes.filter((n) => n.kind === "file");
      state.networkEdges = state.networkEdges.filter((e) => fileIds.has(e.source) && fileIds.has(e.target));
      state.networkExpandedFiles = [];
      state.networkExpandedFunctions = [];
      if (state.networkSelected && !fileIds.has(state.networkSelected)) state.networkSelected = null;
      state.networkMultiSelected = state.networkMultiSelected.filter((id) => fileIds.has(id));
    },
    setNetworkPov: (state, action: PayloadAction<NetworkPov>) => {
      state.networkPov = action.payload;
    },
    setNetworkMode: (state, action: PayloadAction<NetworkMode>) => {
      state.networkMode = action.payload;
    },
    setNetworkHiddenDirs: (state, action: PayloadAction<string[]>) => {
      state.networkHiddenDirs = action.payload;
    },
    toggleNetworkHiddenDir: (state, action: PayloadAction<string>) => {
      const dir = action.payload;
      state.networkHiddenDirs = state.networkHiddenDirs.includes(dir)
        ? state.networkHiddenDirs.filter((d) => d !== dir)
        : [...state.networkHiddenDirs, dir];
    },
    setIndexStatus: (state, action: PayloadAction<IndexStatus | null>) => {
      state.indexStatus = action.payload;
    },
    // --- "Show code" modal ---
    openNetworkCode: (state, action: PayloadAction<{ title: string; subtitle: string }>) => {
      state.networkCodeOpen = true;
      state.networkCodeTitle = action.payload.title;
      state.networkCodeSubtitle = action.payload.subtitle;
      state.networkCodeBody = "";
      state.networkCodeStatus = "loading";
    },
    setNetworkCode: (state, action: PayloadAction<string>) => {
      state.networkCodeBody = action.payload;
      state.networkCodeStatus = "done";
    },
    setNetworkCodeError: (state) => {
      state.networkCodeStatus = "failed";
    },
    closeNetworkCode: (state) => {
      state.networkCodeOpen = false;
    },
    setFileListPagination: (
      state,
      action: PayloadAction<{ full_length: number; page_start: number; page_size: number }>
    ) => {
      state.fileListFullLength = action.payload.full_length;
      state.fileListPageStart = action.payload.page_start;
      state.fileListPageSize = action.payload.page_size;
    },
    setPreview: (state, action: PayloadAction<string>) => {
      state.preview = action.payload;
    },
    clearPreview: (state) => {
      state.preview = undefined;
    },
    setAutomationOutput: (state, action: PayloadAction<string | undefined>) => {
      state.automationOutput = action.payload;
    },
    clearAutomationOutput: (state) => {
      state.automationOutput = undefined;
    },
    setGitOutput: (state, action: PayloadAction<{ stdout: string; stderr: string } | undefined>) => {
      state.gitOutput = action.payload;
    },
    clearGitOutput: (state) => {
      state.gitOutput = undefined;
    },
    setFilterString: (state, action: PayloadAction<string>) => {
      state.filterString = action.payload;
    },
    setCodeSearchString: (state, action: PayloadAction<string>) => {
      state.codeSearchString = action.payload;
    },
    setSearchMode: (state, action: PayloadAction<boolean>) => {
      state.searchMode = action.payload;
    },
    setSearchResult: (state, action: PayloadAction<SearchResult[]>) => {
      state.searchResult = action.payload;
    },
    setViewDeleted: (state, action: PayloadAction<boolean>) => {
      state.viewDeleted = action.payload;
    },

    updateDashLocal: (state, action: PayloadAction<Dash>) => {
      const index = state.dashes.findIndex(dt => dt.id == action.payload.id);
      if (index < 0) return;
      // let newDocumentTemplates = [...state.documentTemplates];
      state.dashes.splice(index, 1, action.payload);

    },

    // For changing the folder to a sub folder.  -1 is home
    setCurrentFolderId: (state, action: PayloadAction<{id: number, name: string}>) => {
      state.currentFolderId = action.payload.id;

      if (state.folderStack[state.folderStack.length - 1] != action.payload.id && action.payload.id != -1) {
        state.folderStack = [...state.folderStack, action.payload.id];
        state.folderNameStack = [...state.folderNameStack, action.payload.name];
      }
      else if (action.payload.id == -1) {
        state.folderStack = [];
        state.folderNameStack = [];
      }
    },
    setFolderStack: (state, action: PayloadAction<Array<number>>) => {
      state.folderStack = action.payload;
    },
    setFolderNameStack: (state, action: PayloadAction<Array<string>>) => {
      state.folderNameStack = action.payload;
    },

    setReposMode: (state, action: PayloadAction<boolean>) => {
      state.reposMode = action.payload;
    },
    setRepoId: (state, action: PayloadAction<number>) => {
      state.repoId = action.payload;
    },
    setFolderName: (state, action: PayloadAction<string>) => {
      state.folderName = action.payload;
    },
    setRepoFolder: (state, action: PayloadAction<string>) => {
      state.repoFolder = action.payload;
    },

    setScenariosMode: (state, action: PayloadAction<boolean>) => {
      state.scenariosMode = action.payload;
    },
    setScenarioId: (state, action: PayloadAction<number>) => {
      state.scenarioId = action.payload;
      if (action.payload < 0) {
        state.scenarioMeasurables = [];
      }
    },
    setScenarioMeasurables: (state, action: PayloadAction<Measurable[]>) => {
      state.scenarioMeasurables = action.payload;
    },

    setRolesMode: (state, action: PayloadAction<boolean>) => {
      state.rolesMode = action.payload;
    },
    setRoleId: (state, action: PayloadAction<number>) => {
      state.roleId = action.payload;
    },
    setUsersInRole: (state, action: PayloadAction<Array<User>>) => {
      state.usersInRole = action.payload;
    },
    setFocusChart: (state, action:PayloadAction<Chart> ) => {
      state.focusChart = action.payload;
    },
    setIsFileListRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isFileListRefreshing = action.payload;
    },
    /*
    setImagePreview: (
      state,
      action: PayloadAction<{
        dash: Dash;
        imagePreview: DashImagePreview;
      }>
    ) => {
      // Get the original document template.
      const index = state.dashes.findIndex(dt => dt.id === action.payload.dash.id);
      if(index !== -1)
        state.dashes[index].imagePreview = action.payload.imagePreview;
      else console.error(`Cannot find DocumentTemplate with ID ${action.payload.dash.id}`);
    },*/

  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteDash.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteDash.fulfilled, (state, action) => {
        state.status = "idle";
        state.dashes = action.payload.dashes;
      })
      .addCase(deleteDash.rejected, (state, action) => {
        state.status = "idle";
        state.error = `${action.error?.code}: ${action.error?.message}`;
      });
  },
});

export const {
  setAutomations,
  setAutoLines,
  setStatus,
  setError,
  setDashes,
  setFolders,
  setHosts,
  setFactotums,
  setProjects,
  setProjectTasks,
  setProjectTaskInputs,
  setProjectTaskOutputs,
  setFactotumStates,
  setFactotumStateTies,
  setPreview,
  clearPreview,
  setAutomationOutput,
  clearAutomationOutput,
  setGitOutput,
  clearGitOutput,
  setRoles,
  setRepos,
  setBookmarks,
  setCurrentFolderId,
  setFolderNameStack,
  setFolderStack,
  //setImagePreview,
  setFilterString,
  setCodeSearchString,

  setSearchMode,
  setSearchResult,

  setRepoId,
  setFolderName,
  setRepoFolder,
  setReposMode,
  setScenariosMode,
  setScenarioId,
  setScenarioMeasurables,

  setFiles,
  setFileSizes,
  setFileModifieds,
  setFileFolders,
  setFileFolderLabels,
  setFileFolderLabel,
  setFileLineCounts,
  openFuncMap,
  closeFuncMap,
  setFuncMapStatus,
  setFuncMapResult,
  setFuncMapError,
  setFuncMapCallees,
  startFuncMapSlice,
  setFuncMapSlice,
  setFuncMapSliceError,
  clearFuncMapSlice,
  openNetwork,
  closeNetwork,
  setNetworkStatus,
  setNetworkGraph,
  setNetworkError,
  addNetworkElements,
  setNetworkSelected,
  toggleNetworkSelected,
  setNetworkMultiSelected,
  clearNetworkSelected,
  clearNetworkNodeColors,
  setNetworkNodeColors,
  collapseNetworkFile,
  collapseAllNetwork,
  enterNetworkFocus,
  exitNetworkFocus,
  setNetworkPov,
  setNetworkMode,
  setNetworkHiddenDirs,
  toggleNetworkHiddenDir,
  openNetworkCode,
  setNetworkCode,
  setNetworkCodeError,
  closeNetworkCode,
  setIndexStatus,
  setFileListPagination,
  setGitInfo,

  setRoleId,
  setRolesMode,
  setUsersInRole,

  setFocusChart,
  setIsFileListRefreshing,
  
  setViewDeleted,
  updateDashLocal
} = librarySlice.actions;

//Selectors
export const selectLibrary = (state: RootState) => state.library;

export const selectCurrentFolder = (state: RootState) => state.library.currentFolderId;
export const selectFolders = (state: RootState) => state.library.folders;
export const selectFolderStack = (state: RootState) => state.library.folderStack;
export const selectFolderNameStack = (state: RootState) => state.library.folderNameStack;

export const selectRolesMode = (state: RootState) => state.library.rolesMode;
export const selectRoleId = (state: RootState) => state.library.roleId;

export const selectReposMode = (state: RootState) => state.library.reposMode;
export const selectRepoId = (state: RootState) => state.library.repoId;
export const selectRepoFolder = (state: RootState) => state.library.repoFolder;
export const selectRepos = (state: RootState) => state.library.repos;
export const selectScenariosMode = (state: RootState) => state.library.scenariosMode;
export const selectScenarioId = (state: RootState) => state.library.scenarioId;
export const selectScenarioMeasurables = (state: RootState) => state.library.scenarioMeasurables;
export const selectHosts = (state: RootState) => state.library.hosts;
export const selectFactotums = (state: RootState) => state.library.factotums;
export const selectProjects = (state: RootState) => state.library.projects;
export const selectProjectTasks = (state: RootState) => state.library.projectTasks;
export const selectProjectTaskInputs = (state: RootState) => state.library.projectTaskInputs;
export const selectProjectTaskOutputs = (state: RootState) => state.library.projectTaskOutputs;
export const selectFactotumStates = (state: RootState) => state.library.factotumStates;
export const selectFactotumStateTies = (state: RootState) => state.library.factotumStateTies;
export const selectAutomations = (state: RootState) => state.library.automations;
export const selectFolderName = (state: RootState) => state.library.folderName;
export const selectBookmarks = (state: RootState) => state.library.bookmarks;

export const selectFiles = (state: RootState) => state.library.files;
export const selectFileFolders = (state: RootState) => state.library.file_folders;
export const selectFileFolderLabels = (state: RootState) => state.library.fileFolderLabels;
export const selectFileLineCounts = (state: RootState) => state.library.fileLineCounts;
export const selectFuncMapOpen = (state: RootState) => state.library.funcMapOpen;
export const selectFuncMapStatus = (state: RootState) => state.library.funcMapStatus;
export const selectFuncMapFile = (state: RootState) => state.library.funcMapFile;
export const selectFuncMapStats = (state: RootState) => state.library.funcMapStats;
export const selectFuncMapMappedFiles = (state: RootState) => state.library.funcMapMappedFiles;
export const selectFuncMapFunctions = (state: RootState) => state.library.funcMapFunctions;
export const selectFuncMapCallees = (state: RootState) => state.library.funcMapCallees;
export const selectFuncMapError = (state: RootState) => state.library.funcMapError;
export const selectFuncMapRepoId = (state: RootState) => state.library.funcMapRepoId;
export const selectFuncMapSliceFor = (state: RootState) => state.library.funcMapSliceFor;
export const selectFuncMapSliceName = (state: RootState) => state.library.funcMapSliceName;
export const selectFuncMapSliceStatus = (state: RootState) => state.library.funcMapSliceStatus;
export const selectFuncMapSliceBlocks = (state: RootState) => state.library.funcMapSliceBlocks;
export const selectFuncMapSliceDirection = (state: RootState) => state.library.funcMapSliceDirection;
export const selectNetworkOpen = (state: RootState) => state.library.networkOpen;
export const selectNetworkRepoId = (state: RootState) => state.library.networkRepoId;
export const selectNetworkStatus = (state: RootState) => state.library.networkStatus;
export const selectNetworkError = (state: RootState) => state.library.networkError;
export const selectNetworkNodes = (state: RootState) => state.library.networkNodes;
export const selectNetworkEdges = (state: RootState) => state.library.networkEdges;
export const selectNetworkSelected = (state: RootState) => state.library.networkSelected;
export const selectNetworkMultiSelected = (state: RootState) => state.library.networkMultiSelected;
export const selectNetworkNodeColors = (state: RootState) => state.library.networkNodeColors;
export const selectNetworkFocusFile = (state: RootState) => state.library.networkFocusFile;
export const selectNetworkPov = (state: RootState) => state.library.networkPov;
export const selectNetworkMode = (state: RootState) => state.library.networkMode;
export const selectNetworkHiddenDirs = (state: RootState) => state.library.networkHiddenDirs;
export const selectNetworkCodeOpen = (state: RootState) => state.library.networkCodeOpen;
export const selectNetworkCodeTitle = (state: RootState) => state.library.networkCodeTitle;
export const selectNetworkCodeSubtitle = (state: RootState) => state.library.networkCodeSubtitle;
export const selectNetworkCodeBody = (state: RootState) => state.library.networkCodeBody;
export const selectNetworkCodeStatus = (state: RootState) => state.library.networkCodeStatus;
export const selectIndexStatus = (state: RootState) => state.library.indexStatus;

export const selectFileListFullLength = (state: RootState) => state.library.fileListFullLength;
export const selectFileListPageStart = (state: RootState) => state.library.fileListPageStart;
export const selectFileListPageSize = (state: RootState) => state.library.fileListPageSize;

export const selectFilteredFiles = (state: RootState) => {
  const filter = state.library.filterString;
  const result = state.library.files.filter(d => d.toLowerCase().includes(filter.toLowerCase()));

  // for (const dt of documentTemplates)
  //   dt._type = "template";
  // for (const dr of documentRecords)
  //   dr._type = "record";
  const list = [...result]

  return list;
}

export const selectFilteredFileSizes = (state: RootState) => {
  const filter = state.library.filterString;
  const result = state.library.files.map((_,i) => ({val:_, idx:i})).filter(d => d.val.toLowerCase().includes(filter.toLowerCase())).map(_ => _.idx);

  // for (const dt of documentTemplates)
  //   dt._type = "template";
  // for (const dr of documentRecords)
  //   dr._type = "record";
  const list = [...result]

  return list;
}

export const selectFilteredFileFolders = (state: RootState) => {
  const filter = state.library.filterString;
  const result = state.library.file_folders.filter(d => d.toLowerCase().includes(filter.toLowerCase()));

  // for (const dt of documentTemplates)
  //   dt._type = "template";
  // for (const dr of documentRecords)
  //   dr._type = "record";
  const list = [...result]

  return list;
}

export const selectDashes = (state: RootState) => state.library.dashes;
export const selectRoles = (state: RootState) => state.library.roles;
export const selectUsersInRole = (state: RootState) => state.library.usersInRole;
export const selectFilteredDashes = (state: RootState) => {
  const filter = state.library.filterString;
  const dashes = state.library.dashes.filter(d => d.name.toLowerCase().includes(filter.toLowerCase()));

  // for (const dt of documentTemplates)
  //   dt._type = "template";
  // for (const dr of documentRecords)
  //   dr._type = "record";
  const list = [...dashes]

  // const list = [...documentTemplates.map(dt => Object.assign({}, dt, {_type: "template"})), ...documentRecords.map(dr => Object.assign({}, dr, {_type: "record"}))]
  // Sort the list.
  list.sort((a,b) => a.name.localeCompare(b.name));
  return list;
}
export const selectFilteredFolders = (state: RootState) => {
  const filter = state.library.filterString;
  const folders = state.library.folders.filter(d => d.name.toLowerCase().includes(filter.toLowerCase()));

  // for (const dt of documentTemplates)
  //   dt._type = "template";
  // for (const dr of documentRecords)
  //   dr._type = "record";
  const list = [...folders]

  // const list = [...documentTemplates.map(dt => Object.assign({}, dt, {_type: "template"})), ...documentRecords.map(dr => Object.assign({}, dr, {_type: "record"}))]
  // Sort the list.
  list.sort((a,b) => a.name.localeCompare(b.name));
  return list;
}

export const selectFocusChart = (state: RootState) => state.library.focusChart;

export default librarySlice.reducer;
